<template>
  <b-modal
    v-if="offer"
    id="modal-offer-dynamic"
    modal-class="offer-dynamic-modal"
    :hide-header="true"
    :hide-footer="true"
    :no-close-on-backdrop="false"
    :no-fade="false"
    body-class="offer-modal-body"
    @hidden="onHidden"
    @shown="onShown"
  >
    <template slot="default">
      <div class="offer-container" :style="{ transform: `scale(${scale})` }">
        <!-- Background Image -->
        <img
          v-if="offer?.image_path_full"
          :src="offer.image_path_full"
          style="display: none"
          @load="bgFailed = false"
          @error="bgFailed = true"
        />
        <div class="offer-background" :style="offerBackgroundStyle"></div>

        <!-- Close Button (top right) -->
        <button class="offer-close-btn" @click="closeModal">
          <span>&times;</span>
        </button>

        <!-- Dynamic Layout Components -->
        <offer-layout-single
          v-if="offer.display_type === 'single' && firstPackage"
          :offer="offer"
          :first-package="firstPackage"
          :display-config="displayConfig"
          :formatted-description="formattedDescription"
          :description-style="descriptionStyle"
          @purchase="handlePurchase"
        />

        <offer-layout-double
          v-else-if="offer.display_type === 'double'"
          :offer="offer"
          :display-config="displayConfig"
          :formatted-description="formattedDescription"
          :description-style="descriptionStyle"
          @purchase="handlePurchase"
        />

        <offer-layout-triple
          v-else-if="offer.display_type === 'triple'"
          :offer="offer"
          :display-config="displayConfig"
          :formatted-description="formattedDescription"
          :description-style="descriptionStyle"
          @purchase="handlePurchase"
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
import OfferLayoutSingle from "./pay/offer-layout-single.vue";
import OfferLayoutDouble from "./pay/offer-layout-double.vue";
import OfferLayoutTriple from "./pay/offer-layout-triple.vue";

export default {
  name: "ModalOfferDynamic",
  components: {
    OfferLayoutSingle,
    OfferLayoutDouble,
    OfferLayoutTriple,
  },
  props: {
    offerData: {
      type: Object,
      required: true,
      default: null,
    },
  },
  data() {
    return {
      scale: 1,
      bgFailed: false,
    };
  },
  computed: {
    offer() {
      return this.offerData;
    },
    firstPackage() {
      return this.offer?.packages?.[0] || null;
    },
    displayConfig() {
      if (!this.offer.display_config) return null;
      try {
        return typeof this.offer.display_config === "string"
          ? JSON.parse(this.offer.display_config)
          : this.offer.display_config;
      } catch (e) {
        console.error("Error parsing display_config:", e);
        return null;
      }
    },
    formattedDescription() {
      if (!this.displayConfig || !this.displayConfig.description) return "";

      const locale = this.$store.state.locale || "de_DE";
      const description =
        this.displayConfig.description[locale] ||
        this.displayConfig.description.fallback ||
        "";

      // Convert markdown-style *text* to <strong>text</strong>
      // Convert \n to <br> for line breaks
      return description
        .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>");
    },
    descriptionStyle() {
      if (!this.displayConfig || !this.displayConfig.style) return {};

      const style = this.displayConfig.style;
      const shadowColor = style["text-shadow-color"];
      return {
        color: style["text-color"] || "#fff",
        "--highlight-color": style["text-highlight-color"] || "#CBC3E3",
        "--text-shadow-color": shadowColor ? shadowColor : "none",
      };
    },
    mapOfferStyles(style = {}) {
      // Allowlist mapping: backend-key -> css
      const map = {
        "text-color": "color",
        "text-highlight-color": "--highlight-color",
        "text-shadow-color": "--text-shadow-color",
        // add more if we need them in future
      };
      const out = {};
      Object.keys(map).forEach((key) => {
        const cssKey = map[key];
        const val = style[key];

        if (val === undefined || val === null || val === "") return;

        out[cssKey] = val;
      });

      // Defaults if missing
      if (!out.color) out.color = "#fff";
      if (!out["--highlight-color"]) out["--highlight-color"] = "#CBC3E3";
      if (!out["--text-shadow-color"])
        out["--text-shadow-color"] = "rgba(0,0,0,0.55)";

      return out;
    },
    offerBackgroundStyle() {
      const url = this.offer?.image_path_full;
      const fallback = "linear-gradient(#444, #444)";
      return {
        backgroundImage: !url || this.bgFailed ? fallback : `url("${url}")`,
      };
    },
  },
  mounted() {
    if (this.offer) {
      this.$bvModal.show("modal-offer-dynamic");
    }
    window.addEventListener("resize", this.calculateScale);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calculateScale);
  },
  methods: {
    closeModal() {
      this.$bvModal.hide("modal-offer-dynamic");
    },
    onHidden() {
      this.$emit("hidden");
    },
    onShown() {
      this.calculateScale();
      // Wait a bit for the DOM to fully render, then fit the text
      this.$nextTick(() => {
        setTimeout(() => {
          this.fitDescriptionText();
        }, 50);
      });
    },
    calculateScale() {
      const modalWidth = 1704;
      const modalHeight = 786;
      const padding = 40;

      const availableWidth = window.innerWidth - padding;
      const availableHeight = window.innerHeight - 60;

      const scaleX = availableWidth / modalWidth;
      const scaleY = availableHeight / modalHeight;

      // Use the smaller scale to ensure modal fits in viewport
      this.scale = Math.min(1, scaleX, scaleY);
    },
    fitDescriptionText() {
      // Find all description elements in the modal using document
      const descriptions = document.querySelectorAll(
        "#modal-offer-dynamic .offer-description"
      );

      if (!descriptions || descriptions.length === 0) return;

      descriptions.forEach((element) => {
        const maxWidth = element.offsetWidth;
        const maxHeight = element.offsetHeight;

        if (!maxWidth || !maxHeight) return;

        // Start with max font size and reduce until text fits
        let fontSize = 30;
        element.style.fontSize = fontSize + "px";

        // Check if content overflows
        while (
          (element.scrollHeight > maxHeight ||
            element.scrollWidth > maxWidth) &&
          fontSize > 14
        ) {
          fontSize -= 1;
          element.style.fontSize = fontSize + "px";
        }

        // Add visible class to fade in the text after resize is complete
        element.classList.add("visible");
      });
    },
    handlePurchase(payPackage) {
      if (
        !payPackage ||
        !payPackage.sku ||
        !payPackage.sku.prices ||
        !payPackage.sku.prices.length
      ) {
        console.error("Invalid package data:", payPackage);
        return;
      }

      this.playSoundTab();

      // Firebase tracking for offer purchase button
      this.firebaseClickItemOffer("offer_buy_button_" + payPackage.id + "#");

      // Get localized package name from display_config
      const locale = this.$store.state.locale || "de_DE";
      const displayConfig =
        typeof payPackage.display_config === "string"
          ? JSON.parse(payPackage.display_config)
          : payPackage.display_config;
      const localizedName =
        displayConfig?.name?.[locale] ||
        displayConfig?.name?.fallback ||
        payPackage.name;

      // Prepare package data for payment modal (similar to old offers)
      const packageData = {
        id: payPackage.id,
        package: {
          id: payPackage.id,
          name: payPackage.name,
          slug: payPackage.slug || "dynamic-offer",
          price: payPackage.sku.prices.find((p) => p.platform === "web")?.price,
          items: payPackage.assets || [],
        },
        rewards: this.parseRewardsFromAssets(payPackage.assets),
        category: "Offer",
        variant: "Dynamic",
        customName: localizedName, // Use localized name from display_config
        customImg: payPackage.image_path_full, // Use package icon
        // Store the original v2 package data and offer ID for payment processing
        v2Package: payPackage,
        v2OfferId: this.offer.id,
      };

      // Store in Vuex for payment modal to access
      this.$store.commit("shop/setShopChosenPackage", packageData);

      // Open payment modal
      this.$bvModal.show("modal-payment");
    },

    /**
     * Parse rewards from v2 assets array into the format expected by payment modal
     * @param {Array} assets - Assets array from v2 API
     * @returns {Object} Rewards object with gold, crown, loyalty, etc.
     */
    parseRewardsFromAssets(assets) {
      const rewards = {
        gold: 0,
        crown: 0,
        loyalty: 0,
        elixir: 0,
        card: 0,
        expbooster: 0,
      };

      if (!assets || !Array.isArray(assets)) {
        return rewards;
      }

      assets.forEach((asset) => {
        let type = "";

        // Try different possible field names for asset type
        if (asset.asset_type || asset.assetType || asset.type) {
          type = (
            asset.asset_type ||
            asset.assetType ||
            asset.type
          ).toLowerCase();
        } else if (asset.asset) {
          // If no type field, extract from PHP class name (e.g., "App\\Models\\Gold" -> "gold")
          const className = asset.asset;
          const lastBackslash = className.lastIndexOf("\\");
          type = (
            lastBackslash >= 0
              ? className.substring(lastBackslash + 1)
              : className
          ).toLowerCase();
        }

        const amount = parseInt(asset.amount) || 0;

        switch (type) {
          case "gold":
            rewards.gold += amount;
            break;
          case "crown":
            rewards.crown += amount;
            break;
          case "loyalty":
            rewards.loyalty += amount;
            break;
          case "elixir":
            rewards.elixir += amount;
            break;
          case "expbooster":
            rewards.expbooster += amount;
            break;
          case "card":
            rewards.card += amount;
            break;
        }
      });

      return rewards;
    },
  },
};
</script>

<style lang="scss">
/* Global backdrop styling - Highest priority for offer modals */
.modal-backdrop.show {
  opacity: 0.8;
  z-index: 9998 !important; /* Ensure backdrop is below modal but above everything else */
}

/* Ensure offer modal has highest z-index */
::v-deep .offer-dynamic-modal {
  z-index: 9999 !important; /* Highest priority - above all other modals */
}
</style>

<style scoped lang="scss">
/* Modal Infrastructure Styling */
::v-deep .offer-dynamic-modal,
::v-deep .offer-dynamic-modal * {
  font-family: "Roboto", sans-serif !important;
  font-weight: 700 !important;
}
::v-deep .offer-dynamic-modal .modal-dialog {
  max-width: none;
  width: auto;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
}

::v-deep .offer-dynamic-modal .modal-content {
  background: transparent;
  border: none;
  box-shadow: none;
  width: auto;
}

::v-deep .offer-dynamic-modal .modal-body {
  padding: 0;
}

.offer-container {
  position: relative;
  width: 1704px;
  height: 786px;
  overflow: hidden;
  transform-origin: center center;
}

.offer-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.offer-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  span {
    color: white;
    font-size: 28px;
    line-height: 0;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -3px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    border-color: white;
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
  }
}
</style>
